import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, CalendarPlus, CheckCircle2, FileUp, FolderKanban, Pencil, Phone } from 'lucide-react';
import { LEAD_STATUS, PRIORITY, dateLabel, dateTimeLabel, type Priority } from '@adisys/shared';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Fact, MetaBadge, Notice, Section, errorText, inr, useCrmMeta, useOpenFile } from '../../lib/crm';
import { PageHeader } from '../../components/AppShell';
import {
  Button, Card, EmptyState, ErrorState, Field, Input, Modal, Select, Skeleton, StatusBadge, Tabs, Textarea, cx, useToast,
} from '../../components/ui';
import { LeadFormModal, StatusButtons, useLeadStatusChange } from './Leads';

type Tab = 'details' | 'followups' | 'documents' | 'timeline';
const KINDS = ['call', 'email', 'whatsapp', 'meeting', 'site_visit', 'other'] as const;

export function LeadDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>('details');
  const [editOpen, setEditOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const statusChange = useLeadStatusChange();

  const q = useQuery({ queryKey: ['crm', 'leads', id], queryFn: () => api.get(`/crm/leads/${id}`) });
  const lead = q.data;

  if (q.isLoading) return <div className="space-y-3 p-6"><Skeleton className="h-10 w-72" /><Skeleton className="h-64 w-full" /></div>;
  if (q.isError || !lead) return <Card className="m-6"><ErrorState error={q.error} onRetry={() => q.refetch()} /></Card>;

  const open = !['converted', 'lost'].includes(lead.status);
  const openFollowups = lead.followups.filter((f: any) => !f.doneAt).length;

  return (
    <>
      <PageHeader title={lead.company}
        description={`${lead.leadCode} · ${lead.contactPerson} · ${lead.location}`}
        actions={<>
          {lead.status !== 'converted' && can('crm.lead.update') && (
            <Button icon={<Pencil className="h-4 w-4" />} onClick={() => setEditOpen(true)}>Edit</Button>)}
          {lead.status === 'qualified' && can('crm.lead.convert') && (
            <Button variant="primary" icon={<FolderKanban className="h-4 w-4" />} onClick={() => setConvertOpen(true)}>Convert to project</Button>)}
        </>}>
        <div className="flex flex-wrap items-center gap-3">
          <MetaBadge meta={LEAD_STATUS} value={lead.status} />
          <StatusBadge tone={PRIORITY.byValue[lead.priority as Priority].tone}>{PRIORITY.byValue[lead.priority as Priority].label} priority</StatusBadge>
          {can('crm.lead.update') && <StatusButtons lead={lead} onRequest={statusChange.request} busy={statusChange.busy} />}
          <Link to="/crm/leads" className="ml-auto text-xs font-medium text-ink-500 hover:underline">All enquiries</Link>
        </div>
      </PageHeader>

      <div className="space-y-4 p-4 sm:p-6">
        {lead.status === 'converted' && lead.convertedProjectId && (
          <Notice tone="success">
            Converted into project{' '}
            <Link to={`/crm/projects/${lead.convertedProjectId}`} className="font-semibold underline">{lead.convertedProjectCode}</Link>.
            The enquiry's history now continues on the project timeline.
          </Notice>)}
        {lead.status === 'lost' && <Notice tone="danger">Lost: {lead.lostReason ?? 'no reason recorded'}.</Notice>}
        {lead.status === 'qualified' && can('crm.lead.convert') && (
          <Notice tone="info">This enquiry is qualified. Convert it to a project to start the site survey.</Notice>)}

        <Card padded={false}>
          <div className="px-2">
            <Tabs<Tab> active={tab} onChange={setTab} tabs={[
              { key: 'details', label: 'Details' },
              { key: 'followups', label: 'Follow-ups', count: openFollowups },
              { key: 'documents', label: 'Documents', count: lead.documents.length },
              { key: 'timeline', label: 'Timeline', count: lead.activity.length },
            ]} />
          </div>
          <div className="p-5">
            {tab === 'details' && <Details lead={lead} />}
            {tab === 'followups' && <Followups lead={lead} canEdit={open && can('crm.lead.update', 'crm.lead.create')} />}
            {tab === 'documents' && <Documents lead={lead} canEdit={open && can('crm.lead.update', 'crm.lead.create')} />}
            {tab === 'timeline' && <Timeline lead={lead} />}
          </div>
        </Card>
      </div>

      <LeadFormModal open={editOpen} onClose={() => setEditOpen(false)} lead={lead} />
      <ConvertModal open={convertOpen} onClose={() => setConvertOpen(false)} lead={lead} />
      {statusChange.modal}
    </>
  );
}

/* ------------------------------------------------------------------- */
function Details({ lead }: { lead: any }) {
  return (
    <div className="space-y-6">
      <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
        <Fact label="Contact person">{lead.contactPerson}</Fact>
        <Fact label="Mobile">{lead.mobile && <a className="hover:underline" href={`tel:${lead.mobile}`}>{lead.mobile}</a>}</Fact>
        <Fact label="Email">{lead.email && <a className="hover:underline" href={`mailto:${lead.email}`}>{lead.email}</a>}</Fact>
        <Fact label="Location">{lead.location}</Fact>
        <Fact label="Industry">{lead.industry}</Fact>
        <Fact label="Project type">{lead.projectType}</Fact>
        <Fact label="Project budget">{lead.budget ? inr(lead.budget) : null}</Fact>
        <Fact label="Source">{lead.source}</Fact>
        <Fact label="Expected closing">{lead.expectedCloseDate ? dateLabel(lead.expectedCloseDate) : null}</Fact>
        <Fact label="Sales executive">{lead.assignedToName}</Fact>
        <Fact label="Created">{dateTimeLabel(lead.createdAt)}</Fact>
        <Fact label="Next follow-up">{lead.nextFollowUpAt ? dateTimeLabel(lead.nextFollowUpAt) : null}</Fact>
      </dl>
      <Section title="Solutions required">
        {lead.solutions.length ? (
          <div className="flex flex-wrap gap-2">{lead.solutions.map((s: string) => <StatusBadge key={s} tone="info" dot={false}>{s}</StatusBadge>)}</div>
        ) : <p className="text-sm text-ink-400">None selected.</p>}
      </Section>
      <Section title="Requirement summary">
        <p className="whitespace-pre-wrap text-sm text-ink-800">{lead.requirementSummary || <span className="text-ink-400">Nothing recorded.</span>}</p>
      </Section>
    </div>
  );
}

/* ------------------------------------------------------------------- */
function Followups({ lead, canEdit }: { lead: any; canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [completing, setCompleting] = useState<any>(null);
  const [outcome, setOutcome] = useState('');
  const [form, setForm] = useState({ kind: 'call', dueAt: '', note: '' });
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['crm', 'leads'] }); void qc.invalidateQueries({ queryKey: ['crm', 'dashboard'] }); };

  const add = useMutation({
    mutationFn: () => api.post(`/crm/leads/${lead.id}/followups`, form),
    onSuccess: () => { refresh(); setAddOpen(false); setForm({ kind: 'call', dueAt: '', note: '' }); toast.success('Follow-up scheduled'); },
    onError: (e) => toast.error('Could not schedule the follow-up', errorText(e)),
  });
  const complete = useMutation({
    mutationFn: () => api.post(`/crm/followups/${completing.id}/complete`, { outcome: outcome || undefined }),
    onSuccess: () => { refresh(); setCompleting(null); setOutcome(''); toast.success('Follow-up completed'); },
    onError: (e) => toast.error('Could not complete the follow-up', errorText(e)),
  });

  return (
    <Section title="Follow-ups" description="Every promised call-back, in order."
      action={canEdit && <Button size="sm" icon={<CalendarPlus className="h-4 w-4" />} onClick={() => setAddOpen(true)}>Schedule</Button>}>
      {lead.followups.length === 0 ? (
        <EmptyState icon={<Phone className="h-5 w-5" />} title="No follow-ups yet" description="Schedule the next touchpoint so this enquiry does not go quiet." />
      ) : (
        <ul className="divide-y divide-line rounded-lg ring-1 ring-line">
          {lead.followups.map((f: any) => {
            const late = !f.doneAt && new Date(f.dueAt) < new Date();
            return (
              <li key={f.id} className="flex flex-wrap items-start gap-3 p-3">
                <CheckCircle2 className={cx('mt-0.5 h-5 w-5 shrink-0', f.doneAt ? 'text-success' : 'text-ink-300')} aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-ink-900"><span className="font-medium capitalize">{f.kind.replace('_', ' ')}</span> · {f.note}</p>
                  <p className={cx('text-xs', late ? 'font-medium text-danger' : 'text-ink-500')}>
                    {f.doneAt ? `Done ${dateTimeLabel(f.doneAt)}` : `${late ? 'Overdue — ' : ''}due ${dateTimeLabel(f.dueAt)}`} · {f.assignedToName}
                  </p>
                  {f.outcome && <p className="mt-1 text-xs text-ink-700">Outcome: {f.outcome}</p>}
                </div>
                {!f.doneAt && canEdit && <Button size="sm" onClick={() => setCompleting(f)}>Mark done</Button>}
              </li>);
          })}
        </ul>
      )}

      <Modal open={addOpen} onClose={() => setAddOpen(false)} size="sm" title="Schedule a follow-up"
        footer={<><Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="primary" loading={add.isPending} disabled={!form.dueAt || form.note.trim().length < 2} onClick={() => add.mutate()}>Schedule</Button></>}>
        <div className="space-y-4">
          <Field label="Type"><Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            {KINDS.map((k) => <option key={k} value={k}>{k.replace('_', ' ')}</option>)}</Select></Field>
          <Field label="When" required><Input type="datetime-local" value={form.dueAt} onChange={(e) => setForm({ ...form, dueAt: e.target.value })} /></Field>
          <Field label="What to cover" required><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
        </div>
      </Modal>
      <Modal open={!!completing} onClose={() => setCompleting(null)} size="sm" title="Complete follow-up"
        description={completing?.note}
        footer={<><Button onClick={() => setCompleting(null)}>Cancel</Button>
          <Button variant="primary" loading={complete.isPending} onClick={() => complete.mutate()}>Mark done</Button></>}>
        <Field label="Outcome" hint="What was agreed?"><Textarea rows={3} value={outcome} onChange={(e) => setOutcome(e.target.value)} /></Field>
      </Modal>
    </Section>
  );
}

/* ------------------------------------------------------------------- */
function Documents({ lead, canEdit }: { lead: any; canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const openFile = useOpenFile();
  const input = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: (files: File[]) => {
      const fd = new FormData();
      fd.set('entityType', 'lead'); fd.set('entityId', lead.id); fd.set('category', 'other');
      files.forEach((f) => fd.append('files', f));
      return api.upload('/crm/documents', fd);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['crm', 'leads', lead.id] }); toast.success('Uploaded'); },
    onError: (e) => toast.error('Upload failed', errorText(e)),
  });

  return (
    <Section title="Documents" description="Requirement notes, drawings, emails — JPG, PNG, WEBP or PDF up to 10 MB."
      action={canEdit && <>
        <input ref={input} type="file" multiple hidden accept="image/jpeg,image/png,image/webp,application/pdf"
          onChange={(e) => { const files = [...(e.target.files ?? [])]; e.target.value = ''; if (files.length) upload.mutate(files); }} />
        <Button size="sm" icon={<FileUp className="h-4 w-4" />} loading={upload.isPending} onClick={() => input.current?.click()}>Upload</Button></>}>
      {lead.documents.length === 0 ? (
        <EmptyState icon={<FileUp className="h-5 w-5" />} title="No documents" description="Attach anything the customer sent." />
      ) : (
        <ul className="divide-y divide-line rounded-lg ring-1 ring-line">
          {lead.documents.map((d: any) => (
            <li key={d.id} className="flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink-900">{d.title}</p>
                <p className="text-xs text-ink-500">{d.uploadedByName ?? 'Unknown'} · {dateTimeLabel(d.createdAt)}</p>
              </div>
              <Button size="sm" onClick={() => void openFile(d.fileId)}>Open</Button>
            </li>))}
        </ul>)}
    </Section>
  );
}

/* ------------------------------------------------------------------- */
function Timeline({ lead }: { lead: any }) {
  if (!lead.activity.length) return <EmptyState title="No activity yet" />;
  return (
    <ol className="space-y-4">
      {lead.activity.map((a: any) => (
        <li key={a.id} className="flex gap-3">
          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-400" aria-hidden />
          <div className="text-sm">
            <p className="text-ink-800">{a.summary}</p>
            <p className="text-xs text-ink-500">{a.actorName ?? 'System'} · {dateTimeLabel(a.createdAt)}</p>
          </div>
        </li>))}
    </ol>
  );
}

/* ------------------------------------------------------------------- */
function ConvertModal({ open, onClose, lead }: { open: boolean; onClose: () => void; lead: any }) {
  const meta = useCrmMeta();
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({ projectName: '', estimatedValue: '', salesExecutiveId: '', scheduleSurvey: false, scheduledAt: '', surveyor: '' });
  const set = (k: keyof typeof form, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const convert = useMutation({
    mutationFn: () => api.post<{ projectId: string }>(`/crm/leads/${lead.id}/convert`, {
      projectName: form.projectName || undefined,
      estimatedValue: form.estimatedValue === '' ? undefined : Number(form.estimatedValue),
      salesExecutiveId: form.salesExecutiveId || undefined,
      survey: form.scheduleSurvey && form.scheduledAt && form.surveyor ? { scheduledAt: form.scheduledAt, assignedTo: form.surveyor } : undefined,
    }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['crm'] });
      toast.success('Project created', 'The enquiry is now a project at the site survey stage.');
      navigate(`/crm/projects/${r.projectId}`);
    },
    onError: (e) => toast.error('Could not convert', errorText(e)),
  });
  const surveyIncomplete = form.scheduleSurvey && (!form.scheduledAt || !form.surveyor);

  return (
    <Modal open={open} onClose={onClose} size="md" title="Convert to project"
      description={`${lead.company} becomes a project and moves to the site survey stage.`}
      footer={<><Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" icon={<ArrowRight className="h-4 w-4" />} loading={convert.isPending} disabled={surveyIncomplete} onClick={() => convert.mutate()}>Create project</Button></>}>
      <div className="space-y-4">
        <Field label="Project name" hint="Leave blank to use the company and solutions."><Input value={form.projectName} onChange={(e) => set('projectName', e.target.value)} /></Field>
        <Field label="Estimated value (₹)" hint={lead.budget ? `The customer's stated budget is ${inr(lead.budget)}.` : undefined}>
          <Input type="number" min={0} value={form.estimatedValue} onChange={(e) => set('estimatedValue', e.target.value)} /></Field>
        {can('crm.lead.view.all') && (
          <Field label="Sales executive"><Select value={form.salesExecutiveId} onChange={(e) => set('salesExecutiveId', e.target.value)}>
            <option value="">Keep {lead.assignedToName ?? 'the current owner'}</option>
            {(meta.data?.people?.sales ?? []).map((p: any) => <option key={p.id} value={p.id}>{p.fullName}</option>)}</Select></Field>)}
        <label className="flex items-center gap-2.5 text-sm text-ink-800">
          <input type="checkbox" className="h-4 w-4 accent-[var(--color-brand-500)]" checked={form.scheduleSurvey}
            onChange={(e) => set('scheduleSurvey', e.target.checked)} />
          Schedule the site survey now
        </label>
        {form.scheduleSurvey && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Survey date and time" required><Input type="datetime-local" value={form.scheduledAt} onChange={(e) => set('scheduledAt', e.target.value)} /></Field>
            <Field label="Surveyor" required><Select value={form.surveyor} onChange={(e) => set('surveyor', e.target.value)}>
              <option value="">Select…</option>
              {(meta.data?.people?.surveyors ?? []).map((p: any) => <option key={p.id} value={p.id}>{p.fullName}</option>)}</Select></Field>
          </div>)}
      </div>
    </Modal>
  );
}
